import { ForwardedRef, forwardRef, HTMLAttributes, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

const TableRoot = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return (
    <table
      className={twMerge(
        "w-full table-auto border-separate border-spacing-0",
        className,
      )}
    >
      {children}
    </table>
  );
};

const Container = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={twMerge(
        "w-full overflow-x-auto rounded-lg border border-solid border-gray-200",
        className,
      )}
    >
      {children}
    </div>
  );
};

const THead = ({ children }: { children: ReactNode }) => {
  return <thead className={"text-left"}>{children}</thead>;
};

const TR = forwardRef(
  (
    props: React.HTMLAttributes<HTMLTableRowElement>,
    ref: ForwardedRef<HTMLTableRowElement>,
  ) => {
    return (
      <tr ref={ref} {...props} className={twMerge("bg-white", props.className)}>
        {props.children}
      </tr>
    );
  },
);

const TH = ({
  children,
  className,
}: {
  children?: ReactNode;
  className?: string;
}) => {
  return (
    <th
      className={twMerge(
        "left-0 border-b border-solid border-gray-200 bg-gray-50 px-4 py-1",
        className,
      )}
    >
      {children}
    </th>
  );
};

const TD = forwardRef(
  (
    props: HTMLAttributes<HTMLTableCellElement>,
    ref: ForwardedRef<HTMLTableCellElement>,
  ) => {
    return (
      <td
        ref={ref}
        {...props}
        className={twMerge(
          "border-b border-solid border-gray-200 px-4 py-1",
          props.className,
        )}
      >
        {props.children}
      </td>
    );
  },
);

const TBody = ({ children }: { children: ReactNode }) => {
  return <tbody className={"divide-y"}>{children}</tbody>;
};

const Grid = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={twMerge(
        "grid auto-rows-fr content-center divide-y *:flex *:items-center *:px-4 *:py-0.5",
        className,
      )}
    >
      {children}
    </div>
  );
};

export {
  TableRoot as TableLegacy,
  Container as TableLegacyContainer,
  THead as TableLegacyTHead,
  TR as TableLegacyTR,
  TH as TableLegacyTH,
  TBody as TableLegacyTBody,
  TD as TableLegacyTD,
  Grid as TableLegacyGrid,
};

TR.displayName = "TableLegacyTR";
TD.displayName = "TableLegacyTD";
